import { type FunctionComponent } from 'react';
import classes from './PractitionerAutocomplete.module.css';
import cx from 'clsx';
import { Autocomplete, AutocompleteProps, Button, Group } from '@mantine/core';
import Icon from '@/components/Icon';
import {
  matchTypes,
  ProviderSearchQueryMatchType,
  ProviderSearchQueryValues,
} from '@/components/ProviderSearch/ProviderSearchForm/types';
import { useDebouncedValue, useUncontrolled } from '@mantine/hooks';
import { useConceptsPractitionersAutocompleteQuery } from '@/lib/graphql';

export interface PractitionerAutocompleteProps extends Omit<
  AutocompleteProps,
  'defaultValue' | 'value' | 'onChange'
> {
  value?: ProviderSearchQueryValues;
  defaultValue?: ProviderSearchQueryValues;
  onChange?: (value: ProviderSearchQueryValues) => void;
}

const PRESET_FACILITIES = [
  { label: 'Urgent Care', value: 'URGENT CARE CENTER' },
  { label: 'Emergency Room', value: 'INPATIENT HOSPITAL' },
  { label: 'Labs', value: 'LABORATORY' },
] as const;

/**
 * PractitionerAutocomplete component allows users to search for practitioners or specialties.
 *
 * This component handles both `query` and `specialtyId` for provider searches. If the `query` matches a `specialtyId`,
 * it will be used for the search. Otherwise, the `query` itself will be used.
 *
 * Both `query` and `specialtyId` can be defined simultaneously. The `query` tracks user input, but only one will be used
 * for the search based on the logic above.
 *
 * If a `specialtyId` is pre-selected, the `query` will be set to the specialty name to display it in the input field,
 * and the `specialtyId` will be used for the search. A query will be made to fetch the name of the pre-selected specialty.
 */
const PractitionerAutocomplete: FunctionComponent<
  PractitionerAutocompleteProps
> = ({ defaultValue, value, onChange, ...inputProps }) => {
  const [_value, setValue] = useUncontrolled({
    value,
    defaultValue,
    finalValue: {
      text: '',
      matchId: undefined,
      matchType: undefined,
    },
    onChange,
  });

  // Debounce value to avoid excessive API calls
  const [debouncedQueryText] = useDebouncedValue(_value.text, 400);

  const { data } = useConceptsPractitionersAutocompleteQuery({
    variables: { query: debouncedQueryText },
  });

  const options = [
    ...(data?.conceptsPractitionersAutocomplete.concepts.map((item) => ({
      label: item.value,
      type: item.type,
      value:
        item.type === 'facility_type'
          ? // facility concepts use the display name as value rather than UUID.
            item.value.toUpperCase()
          : item.id,
    })) ?? []),
    ...(data?.conceptsPractitionersAutocomplete.practitioners.map((item) => ({
      label: item.displayName,
      value: item.id,
      type: 'practitioner',
    })) ?? []),
  ];

  const handleSearchChange = (selectedValue: string) => {
    if (!selectedValue) {
      setValue({
        text: '',
        matchId: undefined,
        matchType: undefined,
      });
      return;
    }

    const { label, value, type } =
      options.find(
        (option) => option.label?.toLowerCase() === selectedValue.toLowerCase()
      ) ?? {};

    if (label && matchTypes.includes(type as ProviderSearchQueryMatchType)) {
      setValue({
        text: label,
        matchId: value,
        matchType: type as ProviderSearchQueryMatchType,
      });
      return;
    }

    setValue({
      text: selectedValue,
      matchId: undefined,
      matchType: undefined,
    });
  };

  const handlePresetClick = (value: string, label: string) => {
    setValue({
      text: label,
      matchId: value,
      matchType: 'facility_type',
    });
  };

  return (
    <div className={cx(classes.root)}>
      <Autocomplete
        {...inputProps}
        value={_value.text}
        data={options}
        onChange={handleSearchChange}
        onOptionSubmit={handleSearchChange}
        onClear={() => handleSearchChange('')}
        clearable={true}
      />

      <Group wrap="nowrap" className={classes.scrollRail}>
        {PRESET_FACILITIES.map(({ label, value }) => (
          <Button
            key={value}
            className={classes.chipButton}
            size="sm"
            variant="outline"
            leftSection={
              <Icon name="feather/search" size="xs" meaning="neutral" />
            }
            onClick={() => handlePresetClick(value, label)}
            aria-label={`Search for ${label}`}>
            {label}
          </Button>
        ))}
      </Group>
    </div>
  );
};

export default PractitionerAutocomplete;
